import { useEffect, useState } from "react";
import { api, formatErr, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, FileDown, Printer, Gauge } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { PaginationBar, usePagination } from "@/components/Pagination";

const emptyForm = { customer_id: "", make: "", model: "", year: "", registration: "", vehicle_type: "motorcycle", odometer: "", status: "Active" };

export default function Vehicles() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(emptyForm);
  const [histFor, setHistFor] = useState(null);
  const [hist, setHist] = useState([]);
  const [fCust, setFCust] = useState("all");
  const [fMake, setFMake] = useState("all");
  const [fModel, setFModel] = useState("all");

  const canEdit = ["owner", "manager", "receptionist"].includes(user.role);
  const canDelete = ["owner", "manager"].includes(user.role);

  const makes = [...new Set(list.map(v => v.make).filter(Boolean))].sort();
  const models = [...new Set(list.filter(v => fMake === "all" || v.make === fMake).map(v => v.model).filter(Boolean))].sort();
  const filtered = list.filter(v =>
    (fCust === "all" || v.customer_id === fCust) &&
    (fMake === "all" || v.make === fMake) &&
    (fModel === "all" || v.model === fModel)
  );
  const pg = usePagination(filtered);

  const load = () => api.get("/vehicles").then(r => setList(r.data));
  useEffect(() => {
    load();
    api.get("/customers").then(r => setCustomers(r.data)).catch(() => {});
  }, []);

  const submit = async () => {
    try {
      const payload = { ...f, year: f.year ? Number(f.year) : null, odometer: Number(f.odometer || 0) };
      if (editId) await api.patch(`/vehicles/${editId}`, payload);
      else await api.post("/vehicles", payload);
      toast.success(editId ? "Vehicle updated" : "Vehicle added");
      setF(emptyForm); setEditId(null); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const edit = (v) => {
    setEditId(v.id);
    setF({
      customer_id: v.customer_id || "", make: v.make || "", model: v.model || "",
      year: v.year ?? "", registration: v.registration || "",
      vehicle_type: v.vehicle_type || "motorcycle", odometer: v.odometer ?? 0,
      status: v.status || "Active",
    });
    setOpen(true);
  };

  const del = async (v) => {
    if (!confirm(`Delete vehicle ${v.registration}? This cannot be undone.`)) return;
    try { await api.delete(`/vehicles/${v.id}`); toast.success("Vehicle deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const openHistory = async (v) => {
    setHistFor(v);
    try { const { data } = await api.get(`/vehicles/${v.id}/odometer-history`); setHist(data); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const exportPdf = async () => {
    try {
      const res = await fetch(`${API}/vehicles/export.pdf`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("erp_token")}` }, credentials: "include",
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = "vehicles_export.pdf"; a.click();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="p-8 print:p-0">
      <div className="print:hidden">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2">Fleet</div>
            <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Vehicles</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="export-pdf-vehicles" size="sm" variant="outline" onClick={exportPdf} className="h-9 rounded-sm text-xs uppercase tracking-wider"><FileDown className="w-3 h-3 mr-1"/> PDF</Button>
            <Button data-testid="print-vehicles" size="sm" variant="outline" onClick={() => window.print()} className="h-9 rounded-sm text-xs uppercase tracking-wider"><Printer className="w-3 h-3 mr-1"/> Print</Button>
            {customers.length > 0 && (
              <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setF(emptyForm); setEditId(null); } }}>
                <DialogTrigger asChild>
                  <Button data-testid="add-vehicle-btn" className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] font-bold text-xs">
                    <Plus className="w-4 h-4 mr-1" /> New Vehicle
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-sm bg-card border-border">
                  <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Edit" : "New"} Vehicle</DialogTitle></DialogHeader>
                  <div className="space-y-3" onKeyDownCapture={handleSimpleFormNav}>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Customer</Label>
                      <Select value={f.customer_id} onValueChange={(v) => setF({ ...f, customer_id: v })}>
                        <SelectTrigger data-testid="veh-customer" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Select customer" /></SelectTrigger>
                        <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name} · {c.phone}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {[["make", "Make"], ["model", "Model"], ["registration", "Registration"], ["year", "Year"], ["odometer", "Current Odometer (km)"]].map(([k, l]) => (
                        <div key={k}>
                          <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{l}</Label>
                          <Input data-testid={`veh-${k}`} value={f[k]} onChange={(e) => setF(x => ({ ...x, [k]: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" />
                        </div>
                      ))}
                      <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Type</Label>
                        <Select value={f.vehicle_type} onValueChange={(v) => setF({ ...f, vehicle_type: v })}>
                          <SelectTrigger data-testid="veh-type" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="motorcycle">Motorcycle</SelectItem>
                            <SelectItem value="scooter">Scooter</SelectItem>
                            <SelectItem value="ev">Electric</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Status</Label>
                        <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                          <SelectTrigger data-testid="veh-status" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Active">Active</SelectItem>
                            <SelectItem value="Inactive">Inactive</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button data-testid="save-vehicle-btn" onClick={submit} disabled={!f.customer_id || !f.make || !f.model || !f.registration} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">{editId ? "Update" : "Save"}</Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3 mb-4 border border-border bg-card rounded-sm p-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Customer</Label>
            <Select value={fCust} onValueChange={setFCust}>
              <SelectTrigger data-testid="filter-customer" className="mt-1 rounded-sm bg-secondary border-border w-48"><SelectValue placeholder="All customers"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Customers</SelectItem>
                {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Make</Label>
            <Select value={fMake} onValueChange={(v) => { setFMake(v); setFModel("all"); }}>
              <SelectTrigger data-testid="filter-make" className="mt-1 rounded-sm bg-secondary border-border w-40"><SelectValue placeholder="All makes"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Makes</SelectItem>
                {makes.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Model</Label>
            <Select value={fModel} onValueChange={setFModel}>
              <SelectTrigger data-testid="filter-model" className="mt-1 rounded-sm bg-secondary border-border w-40"><SelectValue placeholder="All models"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Models</SelectItem>
                {models.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {(fCust !== "all" || fMake !== "all" || fModel !== "all") && (
            <Button data-testid="filter-clear" size="sm" variant="outline" onClick={() => { setFCust("all"); setFMake("all"); setFModel("all"); }} className="rounded-sm text-xs uppercase tracking-wider">Clear</Button>
          )}
          <div className="ml-auto text-xs text-muted-foreground font-mono">{filtered.length} of {list.length} vehicles</div>
        </div>

        <div className="border border-border bg-card rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              <tr>
                <th className="text-left p-3">Registration</th>
                <th className="text-left p-3">Owner Name</th>
                <th className="text-left p-3">Make</th>
                <th className="text-left p-3">Model</th>
                <th className="text-left p-3">Year</th>
                <th className="text-right p-3">Current Odometer</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">No vehicles match the selected filters</td></tr>}
              {pg.pageItems.map(v => (
                <tr key={v.id} data-testid={`vehicle-row-${v.id}`} className="border-t border-border hover:bg-secondary/50">
                  <td className="p-3 font-mono text-xs uppercase">
                    <button data-testid={`veh-reg-${v.id}`} onClick={() => nav(`/vehicles/${v.id}`)} className="text-primary hover:underline" title="Open vehicle details">{v.registration}</button>
                    <div className="flex items-center gap-1 mt-0.5">
                      <span className="text-[10px] text-muted-foreground uppercase">{v.vehicle_type}</span>
                      <button data-testid={`veh-odo-hist-${v.id}`} onClick={() => openHistory(v)} className="text-muted-foreground hover:text-primary" title="Odometer history"><Gauge className="w-3 h-3"/></button>
                    </div>
                  </td>
                  <td className="p-3 text-xs">{v.owner_name || "-"}</td>
                  <td className="p-3">{v.make}</td>
                  <td className="p-3">{v.model}</td>
                  <td className="p-3 font-mono text-xs">{v.year || "-"}</td>
                  <td className="p-3 text-right font-mono text-xs" data-testid={`veh-odo-${v.id}`}>{Number(v.odometer || 0).toLocaleString("en-IN")} km</td>
                  <td className="p-3">
                    <span className={`px-2 py-1 text-[10px] uppercase tracking-wider rounded-sm border ${(v.status || "Active") === "Active" ? "text-green-500 bg-green-500/10 border-green-500/40" : "text-muted-foreground bg-secondary border-border"}`}>{v.status || "Active"}</span>
                  </td>
                  <td className="p-3 text-right space-x-1 whitespace-nowrap">
                    {canEdit && <Button data-testid={`edit-vehicle-${v.id}`} size="sm" variant="ghost" onClick={() => edit(v)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>}
                    {canDelete && <Button data-testid={`del-vehicle-${v.id}`} size="sm" variant="ghost" onClick={() => del(v)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <PaginationBar pg={pg}/>
        </div>
      </div>

      {/* Odometer history dialog */}
      <Dialog open={!!histFor} onOpenChange={(v) => { if (!v) { setHistFor(null); setHist([]); } }}>
        <DialogContent className="rounded-sm bg-card border-border max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display uppercase">Odometer History — {histFor?.registration}</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-muted-foreground mb-2">
            {histFor?.make} {histFor?.model} · Current: <span className="text-primary font-mono">{Number(histFor?.odometer || 0).toLocaleString("en-IN")} km</span>
          </div>
          <div className="border border-border rounded-sm overflow-hidden max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <tr>
                  <th className="text-left p-3">Date</th>
                  <th className="text-right p-3">Odometer Reading</th>
                  <th className="text-left p-3">Reference</th>
                  <th className="text-left p-3">Recorded By</th>
                </tr>
              </thead>
              <tbody>
                {hist.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">No readings recorded yet</td></tr>}
                {hist.map(h => (
                  <tr key={h.id} data-testid={`odo-hist-${h.id}`} className="border-t border-border">
                    <td className="p-3 font-mono text-xs">{h.date}</td>
                    <td className="p-3 text-right font-mono text-xs">{Number(h.odometer).toLocaleString("en-IN")} km</td>
                    <td className="p-3 text-xs">
                      <span className={`px-1.5 py-0.5 text-[9px] uppercase tracking-wider rounded-sm border mr-2 ${h.source === "jobcard" ? "text-blue-400 bg-blue-500/10 border-blue-500/40" : h.source === "booking" ? "text-yellow-500 bg-yellow-500/10 border-yellow-500/40" : "text-muted-foreground bg-secondary border-border"}`}>{h.source}</span>
                      {h.ref}
                    </td>
                    <td className="p-3 text-xs">{h.recorded_by || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Print sheet (hidden except when printing) */}
      <div className="hidden print:block print:bg-white print:text-black" id="print-vehicles">
        <h1 className="text-xl font-bold mb-1">Vehicle Fleet</h1>
        <div className="text-xs mb-3">Rows: {list.length}</div>
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr>
              {["Registration", "Owner Name", "Make", "Model", "Year", "Current Odometer", "Status"].map(h => (
                <th key={h} className="border border-black text-left p-1">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {list.map(v => (
              <tr key={v.id}>
                <td className="border border-black p-1">{v.registration}</td>
                <td className="border border-black p-1">{v.owner_name || "-"}</td>
                <td className="border border-black p-1">{v.make}</td>
                <td className="border border-black p-1">{v.model}</td>
                <td className="border border-black p-1">{v.year || "-"}</td>
                <td className="border border-black p-1">{v.odometer || 0} km</td>
                <td className="border border-black p-1">{v.status || "Active"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
